import { useState, useEffect, useRef } from "react";
import { renderPDFPage, readPDFFile } from "../Utils";

function PDFViewerView(props) {
  //current page number showing
  const [currentPageInfo, setCurrentPageInfo] = useState({
    pageNumber: 0,
    width: 0,
    height: 0,
  });
  //pdf info
  const [pdfInfo, setPdfInfo] = useState({
    pages: [], //array of element {pageIndex, width, height, pageImage, pageNumber}
    password: "",
    locked: false,
    numPages: 0,
  });
  //window dimension
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  //initialized flaf
  const initializedRef = useRef(false);

  //use effect hook
  useEffect(() => {
    //check
    if (initializedRef.current === false) {
      //set
      initializedRef.current = true;
      //render first page
      renderPDFPageAtIndex(props.file, props.password ?? "", 0);
    }

    //add event listner
    window.addEventListener("resize", handleResize);
    //relase
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line
  }, []);

  //handle window resize
  const handleResize = () => {
    //set dimension
    setWindowDimensions({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  };

  //reset screen
  //remove files info
  //empty selected files list
  const resetScreen = () => {
    //callback
    props.onClose();
  };

  //render page at index
  //return image, width, height
  const renderPDFPageAtIndex = async (selectedFile, password, pageIndex) => {
    //check if already rendered
    const filteredArray = pdfInfo.pages.filter(
      (item) => item.pageIndex === pageIndex,
    );

    //check
    if (filteredArray.length > 0) {
      //set page number
      setCurrentPageInfo({
        pageNumber: pageIndex + 1,
        width: filteredArray[0].width,
        height: filteredArray[0].height,
      });
    } else {
      //read pdf file
      await readPDFFile(selectedFile)
        .then(async (data) => {
          //thumbnail
          await renderPDFPage(data, password, "image/jpeg", 1.0, pageIndex)
            .then(([img, numPages, dimension]) => {
              //new page array
              const newPageArray = [
                ...pdfInfo.pages,
                {
                  pageIndex: pageIndex,
                  pageImage: img,
                  width: dimension[0],
                  height: dimension[1],
                  pageNumber: pageIndex + 1,
                },
              ];
              //set values
              setPdfInfo({
                ...pdfInfo,
                pages: newPageArray,
                password: password,
                locked: false,
                numPages: numPages,
              });
              //set page number
              //set page number
              setCurrentPageInfo({
                pageNumber: pageIndex + 1,
                width: dimension[0],
                height: dimension[1],
              });
            })
            .catch((e) => {
              //check
              if (e.name === "PasswordException") {
                //set
                setPdfInfo({ ...pdfInfo, locked: true });
              }
            });
        })
        .catch((e) => {
          console.log("Error: ", e);
        });
    }
  };

  return (
    //show hide loading view
    <div className="pb-5">
      {/* Top bar */}
      <div className="bg-light border-bottom sticky-top">
        <div className="d-flex">
          <button
            type="button"
            className="btn btn-transparent"
            onClick={resetScreen}
          >
            <img
              loading="lazy"
              src="./images/arrow-left-short.svg"
              alt="back"
              style={{ width: "30px", height: "auto" }}
            ></img>
          </button>
          {/* Title */}
          <label className="fs-3 ms-2 py-2 text-secondary fs-4">
            {props.title ?? "PDF Viewer Free"}
          </label>
        </div>
      </div>

      {/* page number */}
      {pdfInfo.locked === false && (
        <div className="text-center mt-3">
          <div className="d-flex justify-content-center align-items-center mb-3">
            {/* Previous button */}
            <button
              className="btn btn-outline-primary me-4"
              style={{ width: "40px" }}
              onClick={async () => {
                //page number
                const pn = Math.max(1, currentPageInfo.pageNumber - 1);
                //enter value
                await renderPDFPageAtIndex(
                  props.file,
                  pdfInfo.password,
                  pn - 1,
                );
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-arrow-left-short"
                viewBox="0 0 16 16"
              >
                <path
                  fillRule="evenodd"
                  d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5a.5.5 0 0 1 .5.5"
                />
              </svg>
            </button>

            {/* page text */}
            <label className="me-3">Page</label>

            {/* page number input */}
            <input
              className="form-control"
              type="number"
              id="pagenumberinput"
              value={currentPageInfo.pageNumber}
              onChange={async (e) => {
                //set page number
                //clamp page number to range
                //page number
                const pn = Math.max(
                  Math.min(pdfInfo.numPages, e.target.value),
                  1,
                );
                //enter value
                await renderPDFPageAtIndex(
                  props.file,
                  pdfInfo.password,
                  pn - 1,
                );
              }}
              style={{ width: "80px", maxWidth: "100px" }}
            />

            {/* total pages input */}
            <label className="ms-3">of {pdfInfo.numPages}</label>

            {/* next page button */}
            <button
              className="btn btn-outline-primary ms-4"
              style={{ width: "40px" }}
              onClick={async () => {
                //page number
                const pn = Math.min(
                  pdfInfo.numPages,
                  currentPageInfo.pageNumber + 1,
                );
                //enter value
                await renderPDFPageAtIndex(
                  props.file,
                  pdfInfo.password,
                  pn - 1,
                );
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-arrow-right-short"
                viewBox="0 0 16 16"
              >
                <path
                  fillRule="evenodd"
                  d="M4 8a.5.5 0 0 1 .5-.5h5.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H4.5A.5.5 0 0 1 4 8"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* render pdf document */}
      {/* show password inpur */}
      {pdfInfo.locked ? (
        <div
          className="d-flex text-center justify-content-center align-items-center"
          style={{ width: "100%", height: windowDimensions.height - 200 }}
        >
          <div className="mx-auto" style={{ width: "30%", minWidth: "200px" }}>
            {/* text */}
            <p
              className="text-secondary"
              style={{ overflowWrap: "break-word" }}
            >
              Enter password to unlock{" "}
              <b>{props.file ? props.file.name : ""}</b>
            </p>
            {/* password input */}
            <div className="mt-3">
              <input
                className="form-control border border-primary rounded-2"
                placeholder="Enter password to unlock pdf"
                id="password_input"
                type="password"
              />
            </div>
            {/* button */}
            <button
              className="btn btn-primary mt-4 px-4"
              onClick={async (e) => {
                //get element
                const passElem = document.getElementById("password_input");
                //check
                if (passElem !== null) {
                  await renderPDFPageAtIndex(
                    props.file,
                    passElem.value,
                    Math.max(0, currentPageInfo.pageNumber - 1),
                  );
                }
              }}
            >
              UNLOCK
            </button>
          </div>
        </div>
      ) : (
        <div className="d-flex text-center justify-content-center align-items-center">
          <img
            loading="lazy"
            style={{
              width: `${
                windowDimensions.width > currentPageInfo.width
                  ? currentPageInfo.width
                  : windowDimensions.width
              }px`,
            }}
            src={
              pdfInfo.pages.filter(
                (item) => item.pageNumber === currentPageInfo.pageNumber,
              ).length > 0
                ? pdfInfo.pages.filter(
                    (item) => item.pageNumber === currentPageInfo.pageNumber,
                  )[0].pageImage
                : null
            }
            alt="loading..."
          />
        </div>
      )}
    </div>
  );
}

export default PDFViewerView;
